import { useState, useMemo, useEffect, useRef } from "react";
import { CaretRightIcon, SidebarSimpleIcon, ArrowLeftIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { bucketUrlPrefix } from "common/config";
import { Tooltip } from "react-tooltip";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
} from "common/types";
import { VenueDetailsBySlugResponse, useI18N, useAuth } from "common/hooks";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import FullImageViewer from "@/components/FullImageViewer";
import { useDarkMode } from "@/hooks/useDarkMode";
import { cn } from "@/lib/utils";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
  actionToPerformOwnerAtom,
} from "@/store/atoms";

// ============================================================================
// Types
// ============================================================================

interface PhotoItem {
  path: string;
  alt_text?: string;
  category_name: string;
  category_id: number;
  tag_name: string;
  created_at: string;
  photo_type?: string;
  review_photos_id?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  venueType?: "Basic" | "Hybrid";
  qtags?: Array<{
    tagId: number;
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  tags?: Array<{
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  displaySections?: Record<string, string>;
  user_roles?: {
    isGeneralUser: boolean;
  };
  approved_for_public?: boolean;
}

interface CategoryInfo {
  id: number;
  name: string;
  image: string;
  image_dark: string;
}

interface SubcategoryItem {
  id: string;
  name: string;
  categoryId: number;
  categoryName: string;
  photos: PhotoItem[];
}

interface PhotosDetailViewProps {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  accessibilityCategories?: AccessibilityCategory[];
  accessibilityCategoriesHybrid?: AccessibilityCategoryHybrid[];
  initialCategoryId?: number | null;
  onClose?: () => void;
  venueName: string;
}

// ============================================================================
// Sub-components
// ============================================================================

// --- Navigation Header ---
interface NavigationHeaderProps {
  venueName: string;
  onToggleSidebar: () => void;
  onBack: () => void;
  isSidebarOpen: boolean;
  activeTab: string;
  onTabChange: (tab: string) => void;
  hasSociabilityPhotos: boolean;
  hasUserPhotos: boolean;
  sociabilityPhotoCount: number;
  userPhotoCount: number;
}

function NavigationHeader({
  venueName,
  onToggleSidebar,
  onBack,
  isSidebarOpen,
  activeTab,
  onTabChange,
  hasSociabilityPhotos,
  hasUserPhotos,
  sociabilityPhotoCount,
  userPhotoCount,
}: NavigationHeaderProps) {
  const i18n = useI18N();

  return (
    <header className="flex flex-col bg-bg-brand-secondary-default relative z-10">
      {/* Row 1: Breadcrumb Navigation */}
      <div className="flex items-center gap-2 px-5 pt-3">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2">
          {/* Home / Venue Name */}
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors"
            aria-label="Back to venue details"
          >
            <ArrowLeftIcon size={20} className="flex-shrink-0 text-fg-neutral-strong" />
            <span className="label-m-medium truncate max-w-[300px] lg:max-w-[400px]">
              {venueName}
            </span>
          </button>
        </nav>
      </div>

      {/* Row 2: Controls */}
      <div className="flex items-center gap-3 p-3 border-b border-border-token-brand-minimal">
        {/* Sidebar Toggle Button - Show when collapsed */}
        {!isSidebarOpen && (
          <>
            <button
              id="photos-sidebar-toggle-btn"
              type="button"
              onClick={onToggleSidebar}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
              aria-label={i18n.get("toggleSidebar")}
              aria-expanded={isSidebarOpen}
            >
              <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#photos-sidebar-toggle-btn"
              content={i18n.get("toggleSidebar")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <div
              className="hidden lg:block w-px h-6 border border-border-token-neutral-minimal"
              aria-hidden="true"
            />
          </>
        )}

        {/* UserIcon type tabs - By Sociability / By UsersIcon styled as category pills */}
        {(hasSociabilityPhotos || hasUserPhotos) && (
          <div className="flex gap-1 items-center flex-1 overflow-x-auto">
            {hasSociabilityPhotos && (
              <button
                type="button"
                onClick={() => {
                  onTabChange("by-sociability");
                }}
                className={cn(
                  "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors focus:outline-none",
                  activeTab === "by-sociability"
                    ? "bg-container-brand-minimal border border-border-token-brand-minimal body-l-semibold text-fg-brand-subtle"
                    : "label-m-medium text-fg-neutral-subtle hover:bg-bg-brand-secondary-hover",
                )}
                aria-pressed={activeTab === "by-sociability"}
              >
                {i18n.get("bySociability")} ({sociabilityPhotoCount})
              </button>
            )}
            {hasUserPhotos && (
              <button
                type="button"
                onClick={() => {
                  onTabChange("by-users");
                }}
                className={cn(
                  "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors focus:outline-none",
                  activeTab === "by-users"
                    ? "bg-container-brand-minimal border border-border-token-brand-minimal body-l-semibold text-fg-brand-subtle"
                    : "label-m-medium text-fg-neutral-subtle hover:bg-bg-brand-secondary-hover",
                )}
                aria-pressed={activeTab === "by-users"}
              >
                {i18n.get("byUsers")} ({userPhotoCount})
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

// --- Category Sidebar (Tree Navigation) ---
interface CategorySidebarProps {
  subcategories: SubcategoryItem[];
  categories: CategoryInfo[];
  selectedSubcategory: string | null;
  onSelectSubcategory: (subcategoryId: string) => void;
  expandedCategories: Set<number>;
  onToggleCategory: (categoryId: number) => void;
  onToggleSidebar: () => void;
  isSidebarOpen?: boolean;
}

function CategorySidebar({
  subcategories,
  categories,
  selectedSubcategory,
  onSelectSubcategory,
  expandedCategories,
  onToggleCategory,
  onToggleSidebar,
  isSidebarOpen = true,
}: CategorySidebarProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const [searchQuery, setSearchQuery] = useState("");

  // Count non-empty subcategories
  const subcategoryCount = useMemo(() => {
    return subcategories.length;
  }, [subcategories]);

  // Filter categories and subcategories based on search query
  const { filteredCategories, filteredSubcategoriesByCategory } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      // No search query - return all
      const grouped: Record<number, SubcategoryItem[]> = {};
      subcategories.forEach((subcategory) => {
        if (!grouped[subcategory.categoryId]) {
          grouped[subcategory.categoryId] = [];
        }
        grouped[subcategory.categoryId].push(subcategory);
      });
      return {
        filteredCategories: categories,
        filteredSubcategoriesByCategory: grouped,
      };
    }

    // Filter subcategories that match the search query
    const matchingSubcategories = subcategories.filter((subcategory) => {
      return (
        subcategory.name.toLowerCase().includes(query) ||
        subcategory.categoryName.toLowerCase().includes(query)
      );
    });

    // Group matching subcategories by category
    const grouped: Record<number, SubcategoryItem[]> = {};
    matchingSubcategories.forEach((subcategory) => {
      if (!grouped[subcategory.categoryId]) {
        grouped[subcategory.categoryId] = [];
      }
      grouped[subcategory.categoryId].push(subcategory);
    });

    // Filter categories that have matching subcategories or match the query themselves
    const matchingCategories = categories.filter((category) => {
      return (
        category.name.toLowerCase().includes(query) ||
        grouped[category.id]?.length > 0
      );
    });

    return {
      filteredCategories: matchingCategories,
      filteredSubcategoriesByCategory: grouped,
    };
  }, [subcategories, categories, searchQuery]);

  return (
    <nav
      className="flex flex-col h-full bg-bg-brand-secondary-default border-r border-border-token-brand-minimal"
      aria-label="Photo categories navigation"
    >
      {/* Header with count and close button */}
      <div className="flex items-center justify-between pt-4 pl-5 pr-4">
        <h2 className="label-l-bold text-fg-neutral-strong">
          {subcategoryCount} Categories
        </h2>
        <button
          id="photos-sidebar-close-btn"
          type="button"
          onClick={onToggleSidebar}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
          aria-label={i18n.get("closeSidebar")}
          aria-expanded={isSidebarOpen}
        >
          <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
        </button>
        <Tooltip
          anchorSelect="#photos-sidebar-close-btn"
          content={i18n.get("closeSidebar")}
          place="bottom"
          style={{ zIndex: 9999 }}
        />
      </div>

      {/* Search Input */}
      <div className="pt-4 pl-4 pr-4">
        <div className="relative">
          <MagnifyingGlassIcon
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-neutral-subtle"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={i18n.get("searchLabel")}
            className="w-full  h-12  pl-10 pr-4 py-2 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-lg text-base md:text-[13px] text-fg-neutral-strong placeholder:text-fg-neutral-subtle focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="label-m-medium text-fg-neutral-subtle">
              {i18n.get("noCategoriesFoundMatching")} "{searchQuery}"
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-6">
            {filteredCategories.map((category) => {
              const categorySubcategories =
                filteredSubcategoriesByCategory[category.id] || [];
              const isExpanded = expandedCategories.has(category.id);
              const hasSubcategories = categorySubcategories.length > 0;

              return (
                <li key={category.id}>
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => {
                      if (hasSubcategories) {
                        onToggleCategory(category.id);
                      }
                    }}
                    className="flex items-center gap-4 w-full py-2 text-left"
                    aria-expanded={isExpanded}
                  >
                    {category.image && (
                      <img
                        src={
                          bucketUrlPrefix +
                          (isDark && category.image_dark
                            ? category.image_dark
                            : category.image)
                        }
                        alt={category.name}
                        className={`w-6 h-6 flex-shrink-0 ${isDark && !category.image_dark ? "filter brightness-0 invert" : ""}`}
                      />
                    )}

                    <span className="flex-1 label-m-semibold text-fg-neutral-strong">
                      {category.name}
                    </span>

                    {hasSubcategories && (
                      <CaretRightIcon
                        size={16}
                        className={`text-fg-neutral-strong transition-transform ${isExpanded ? "-rotate-90" : "rotate-90"}`}
                      />
                    )}
                  </button>

                  {/* Subcategories */}
                  {isExpanded && hasSubcategories && (
                    <ul className="mt-1 ml-3 border-l-2 border-border-token-brand-minimal">
                      {categorySubcategories.map((subcategory) => {
                        const isSubcategorySelected =
                          selectedSubcategory === subcategory.id;

                        return (
                          <li key={subcategory.id}>
                            <button
                              type="button"
                              onClick={() => {
                                onSelectSubcategory(subcategory.id);
                              }}
                              className={`flex items-center gap-3 w-full pl-4 pr-3 py-2 text-left rounded-r-lg transition-colors ${
                                isSubcategorySelected
                                  ? "bg-container-brand-minimal border-l-2 border-primary-500 -ml-0.5"
                                  : "hover:bg-bg-brand-secondary-hover"
                              }`}
                              aria-current={
                                isSubcategorySelected ? "true" : undefined
                              }
                            >
                              <span
                                className={`flex-1 label-m-medium ${
                                  isSubcategorySelected
                                    ? "text-fg-brand-subtle"
                                    : "text-fg-neutral-strong"
                                }`}
                              >
                                {subcategory.name}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* Empty Category Message */}
                  {isExpanded && !hasSubcategories && (
                    <div className="ml-10 mt-1 label-m-medium text-fg-neutral-minimal">
                      No {category.name.toLowerCase()} found
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </nav>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function PhotosDetailView({
  venueDetailsResponse: data,
  accessibilityCategories,
  initialCategoryId,
  onClose,
  venueName,
}: PhotosDetailViewProps) {
  const venueDetails = data?.details;

  const [subcategories, setSubcategories] = useState<SubcategoryItem[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
    null,
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set()
  );
  // Initialize showSidebar based on screen size
  const [showSidebar, setShowSidebar] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [fullImageView, setFullImageView] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentSubcategoryPhotos, setCurrentSubcategoryPhotos] = useState<
    PhotoItem[]
  >([]);

  // UserIcon type filter states
  const [activeTab, setActiveTab] = useState<string>("by-sociability");
  const [hasSociabilityPhotos, setHasSociabilityPhotos] = useState(false);
  const [hasUserPhotos, setHasUserPhotos] = useState(false);
  const [sociabilityPhotoCount, setSociabilityPhotoCount] = useState(0);
  const [userPhotoCount, setUserPhotoCount] = useState(0);
  const [allPhotosData, setAllPhotosData] = useState<SubcategoryItem[]>([]); // Store unfiltered data

  // Flag photo state
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<PhotoItem | null>(null);

  // Auth hooks
  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom<any>(actionToPerformValueAfterLoginAtom);
  const [actionToPerformOwner, setActionToPerformOwner] = useAtom(
    actionToPerformOwnerAtom,
  );
  // Distinct owner id: the full-screen photos overlay. Resumes only the flag a
  // user started here, never the basic-sheet photo tab mounted behind it.
  const FLAG_OWNER = "photo:detailView";

  // Resume a photo flag begun while logged out — only this owner reacts.
  useEffect(() => {
    if (
      isLoggedIn &&
      user &&
      actionToPerformAfterLogin === "flagPhoto" &&
      actionToPerformOwner === FLAG_OWNER
    ) {
      setSelectedImage(actionToPerformValueAfterLogin);
      setIsFlagVenuePhotoOpen(true);
      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
      setActionToPerformOwner("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, actionToPerformOwner, user, isLoggedIn]);

  const categoryRefs = useRef<Record<number, HTMLElement>>({});
  const subcategoryRefs = useRef<Record<string, HTMLElement>>({});

  // Extract and organize photos by category and subcategory
  useEffect(() => {
    if (!data || !accessibilityCategories) return;

    const allSubcategories: SubcategoryItem[] = [];
    const categoriesInfo: CategoryInfo[] = [];
    let sociabilityCount = 0;
    let userCount = 0;

    accessibilityCategories.forEach((category) => {
      categoriesInfo.push({
        id: category.catId,
        name: category.catName,
        image: category.catImage || "",
        image_dark: category.catImageDark || "",
      });

      category.tags.forEach((tag, tagIndex) => {
        const photos: PhotoItem[] = [];

        // Get the first tag name from qtags
        const firstTagName =
          tag.qtags && tag.qtags.length > 0 ? tag.qtags[0].tagName : "Unknown";

        // Extract photos from reviewPhotos - collect ALL photos without filtering
        tag.reviewPhotos?.forEach((photo) => {
          // Count photos by type
          // Walk-through: mapper/team photos + approved general-user photos
          if (
            photo.user_roles?.isGeneralUser !== true ||
            photo.approved_for_public === true
          ) {
            sociabilityCount++;
          }
          // By Users: only non-approved general-user photos
          if (
            photo.user_roles?.isGeneralUser === true &&
            photo.approved_for_public !== true
          ) {
            userCount++;
          }

          photos.push({
            path: photo.path,
            alt_text: photo.alt_text,
            category_name: category.catName,
            category_id: category.catId,
            tag_name: firstTagName,
            created_at: photo.created_at || "",
            photo_type: "review",
            review_photos_id: photo.id?.toString(),
            width: photo.width || 800,
            height: photo.height || 600,
            sort_order: photo.sort_order,
            venueType: "Basic",
            qtags: tag.qtags?.map((q) => ({
              tagId: q.tagId,
              tagName: q.tagName,
              categoryDisplaySectionId: q.categoryDisplaySectionId?.toString(),
            })),
            tags: tag.qtags?.map((q) => ({
              tagName: q.tagName,
              categoryDisplaySectionId: q.categoryDisplaySectionId?.toString(),
            })),
            displaySections:
              category.cat_display_sections &&
              typeof category.cat_display_sections === "object"
                ? Object.entries(category.cat_display_sections).reduce(
                    (acc: Record<string, string>, [key, value]) => {
                      if (typeof value === "string") {
                        acc[key] = value;
                      }
                      return acc;
                    },
                    {} as Record<string, string>,
                  )
                : {},
            user_roles: photo.user_roles,
            approved_for_public: photo.approved_for_public,
          });
        });

        if (photos.length > 0) {
          const subcategoryId = `${category.catId}-${tagIndex}`;
          allSubcategories.push({
            id: subcategoryId,
            name: firstTagName,
            categoryId: category.catId,
            categoryName: category.catName,
            photos: photos.sort(
              (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
            ),
          });
        }
      });
    });

    // Store all photos data
    setAllPhotosData(allSubcategories);

    // Set photo type flags and counts
    setHasSociabilityPhotos(sociabilityCount > 0);
    setHasUserPhotos(userCount > 0);
    setSociabilityPhotoCount(sociabilityCount);
    setUserPhotoCount(userCount);

    // Set default tab
    if (sociabilityCount > 0) {
      setActiveTab("by-sociability");
    } else if (userCount > 0) {
      setActiveTab("by-users");
    }

    setCategories(categoriesInfo);

    // Initialize all categories as expanded
    setExpandedCategories(new Set(categoriesInfo.map((c) => c.id)));

    // Auto-select first category if initialCategoryId is not provided
    if (!initialCategoryId && categoriesInfo.length > 0) {
      setSelectedCategory(categoriesInfo[0].id);
    }
  }, [data, accessibilityCategories, initialCategoryId]);

  // Filter subcategories based on active tab
  useEffect(() => {
    if (allPhotosData.length === 0) return;

    const filtered = allPhotosData
      .map((subcategory) => ({
        ...subcategory,
        photos: subcategory.photos.filter((photo) => {
          if (activeTab === "by-sociability") {
            // Walk-through: mapper/team photos + approved general-user photos
            return (
              photo.user_roles?.isGeneralUser !== true ||
              photo.approved_for_public === true
            );
          } else if (activeTab === "by-users") {
            // By Users: only non-approved general-user photos
            return (
              photo.user_roles?.isGeneralUser === true &&
              photo.approved_for_public !== true
            );
          }
          return true;
        }),
      }))
      .filter((subcategory) => subcategory.photos.length > 0);

    setSubcategories(filtered);
  }, [allPhotosData, activeTab]);

  // Flat array of all photos across all subcategories, filtered by active tab
  const allSubcategoryPhotosFlatted = useMemo(
    () => subcategories.flatMap((s) => s.photos),
    [subcategories],
  );

  // Update selected category based on scroll position (for visual feedback only)
  const updateSelectedCategoryFromScroll = (categoryId: number) => {
    setSelectedCategory(categoryId);
  };

  const handleToggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleSelectSubcategory = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);

    const subcategoryRef = subcategoryRefs.current[subcategoryId];
    const scrollContainer = document.querySelector(".photos-content-area");

    if (subcategoryRef && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = subcategoryRef.getBoundingClientRect();
      const offset = 20; // Small offset from top

      const scrollPosition =
        scrollContainer.scrollTop +
        (elementRect.top - containerRect.top) -
        offset;

      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }

    // Close sidebar on mobile after selecting a subcategory
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowSidebar(false);
    }
  };

  const handleOpenFullImage = (photos: PhotoItem[], index: number) => {
    const clickedPhoto = photos[index];
    const globalIndex = allSubcategoryPhotosFlatted.findIndex(
      (p) => p === clickedPhoto,
    );
    setCurrentImageIndex(globalIndex >= 0 ? globalIndex : index);
    setCurrentSubcategoryPhotos(allSubcategoryPhotosFlatted);
    setFullImageView(true);
    // Remove focus from the button to prevent blue border after closing modal
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleFlagVenuePhotoClose = () => {
    setIsFlagVenuePhotoOpen(false);
  };

  const handleFlagPhoto = (image: any) => {
    if (!user || !isLoggedIn) {
      setActionToPerformAfterLogin("flagPhoto");
      setActionToPerformValueAfterLogin(image);
      setActionToPerformOwner(FLAG_OWNER);
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  // Handle responsive sidebar state on window resize
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      // Only auto-adjust on resize if transitioning between mobile and desktop
      if (isDesktop && !showSidebar) {
        // UserIcon resized to desktop, open sidebar by default
        setShowSidebar(true);
      } else if (!isDesktop && showSidebar) {
        // UserIcon resized to mobile, close sidebar by default
        setShowSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showSidebar]);

  // Handle ESC key to close sidebar on mobile
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSidebar && window.innerWidth < 1024) {
        setShowSidebar(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSidebar]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (showSidebar && typeof window !== 'undefined' && window.innerWidth < 1024) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showSidebar]);

  // Scroll spy to update selected category and subcategory
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector(".photos-content-area");
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const scrollThreshold = containerRect.top + 100; // CheckIcon which element is near top

      // CheckIcon subcategories first (more specific)
      let foundSubcategory = false;
      Object.entries(subcategoryRefs.current).forEach(
        ([subcategoryId, element]) => {
          const rect = element.getBoundingClientRect();
          if (rect.top <= scrollThreshold && rect.bottom > scrollThreshold) {
            if (selectedSubcategory !== subcategoryId) {
              setSelectedSubcategory(subcategoryId);

              // Find and set the parent category
              const subcategory = subcategories.find(
                (s) => s.id === subcategoryId,
              );
              if (subcategory && selectedCategory !== subcategory.categoryId) {
                updateSelectedCategoryFromScroll(subcategory.categoryId);
              }
            }
            foundSubcategory = true;
          }
        },
      );

      // If no subcategory is in view, check categories
      if (!foundSubcategory) {
        Object.entries(categoryRefs.current).forEach(
          ([categoryIdStr, element]) => {
            const rect = element.getBoundingClientRect();
            if (rect.top <= scrollThreshold && rect.bottom > scrollThreshold) {
              const categoryId = parseInt(categoryIdStr);
              if (selectedCategory !== categoryId) {
                updateSelectedCategoryFromScroll(categoryId);
              }
            }
          },
        );
      }
    };

    const contentArea = document.querySelector(".photos-content-area");
    if (contentArea) {
      contentArea.addEventListener("scroll", handleScroll, { passive: true });
      return () => contentArea.removeEventListener("scroll", handleScroll);
    }
  }, [selectedCategory, selectedSubcategory, subcategories]);

  // Group subcategories by category for rendering
  const subcategoriesByCategory = useMemo(() => {
    const grouped: Record<number, SubcategoryItem[]> = {};
    subcategories.forEach((subcategory) => {
      if (!grouped[subcategory.categoryId]) {
        grouped[subcategory.categoryId] = [];
      }
      grouped[subcategory.categoryId].push(subcategory);
    });
    return grouped;
  }, [subcategories]);

  if (!venueDetails) {
    return null;
  }

  return (
    <div className="flex flex-col lg:flex-row h-full bg-bg-brand-secondary-default rounded-xl overflow-hidden">
      {/* Backdrop for mobile drawer - only visible on mobile when sidebar is open */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowSidebar(false)}
          aria-hidden="true"
        />
      )}

      {/* Left Sidebar - Category Navigation */}
      {/* Desktop: inline with transition, Mobile: full-width overlay drawer */}
      <div
        className={`
          lg:relative lg:overflow-hidden
          ${showSidebar ? "lg:w-[314px] lg:flex-shrink-0" : "lg:w-0"}
          lg:transition-all lg:duration-300
        `}
      >
        {/* Mobile: Full-width fixed overlay drawer, Desktop: normal inline content */}
        <div
          className={`
            fixed top-0 left-0 right-0 bottom-0 w-full z-[100]
            transform transition-transform duration-300 ease-in-out
            ${showSidebar ? "translate-x-0" : "-translate-x-full"}
            lg:relative lg:inset-auto lg:w-full lg:transform-none lg:transition-none lg:z-auto
          `}
          role="dialog"
          aria-modal="true"
          aria-label="Photo categories sidebar"
        >
          <CategorySidebar
            subcategories={subcategories}
            categories={categories}
            selectedSubcategory={selectedSubcategory}
            onSelectSubcategory={handleSelectSubcategory}
            expandedCategories={expandedCategories}
            onToggleCategory={handleToggleCategory}
            onToggleSidebar={() => setShowSidebar(!showSidebar)}
            isSidebarOpen={showSidebar}
          />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <NavigationHeader
          venueName={venueName}
          onToggleSidebar={() => setShowSidebar(!showSidebar)}
          onBack={onClose || (() => {})}
          isSidebarOpen={showSidebar}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasSociabilityPhotos={hasSociabilityPhotos}
          hasUserPhotos={hasUserPhotos}
          sociabilityPhotoCount={sociabilityPhotoCount}
          userPhotoCount={userPhotoCount}
        />

        {/* Photos Grid - All categories */}
        <div className="flex-1 overflow-y-auto scrollbar-hide p-6 photos-content-area">
          {categories.map((category) => {
            const categorySubcategories =
              subcategoriesByCategory[category.id] || [];

            if (categorySubcategories.length === 0) return null;

            return (
              <div
                key={category.id}
                ref={(el) => {
                  if (el) {
                    categoryRefs.current[category.id] = el;
                  }
                }}
                className="mb-12 scroll-mt-20"
              >
                {/* Category Title */}
                <div className="mb-6">
                  <h2 className="title-s-semibold text-fg-neutral-strong">
                    {category.name} 
                    <span className="title-s-semibold text-fg-neutral-strong ml-2">
                      ({categorySubcategories.reduce((sum, subcategory) => sum + subcategory.photos.length, 0)})
                    </span>
                  </h2>
                </div>

                {/* Subcategories */}
                <div className="space-y-8">
                  {categorySubcategories.map((subcategory) => (
                    <div
                      key={subcategory.id}
                      ref={(el) => {
                        if (el) {
                          subcategoryRefs.current[subcategory.id] = el;
                        }
                      }}
                      className="scroll-mt-20"
                    >
                      <div className="mb-4">
                        <h3 className="title-s-semibold text-fg-neutral-strong">
                          {subcategory.name} • {subcategory.photos.length} photo
                          {subcategory.photos.length !== 1 ? "s" : ""}
                        </h3>
                      </div>

                      {subcategory.photos.length > 0 && (
                        <div className="photo-grid-container">
                          <RowsPhotoAlbum
                            photos={subcategory.photos.map((photo) => ({
                              src: bucketUrlPrefix + photo.path,
                              width: photo.width || 800,
                              height: photo.height || 600,
                              alt: photo.alt_text || "",
                            }))}
                            spacing={8}
                            targetRowHeight={150}
                            rowConstraints={(containerWidth) => {
                              let maxPhotos = 2;
                              if (containerWidth > 768) maxPhotos = 3;
                              if (containerWidth > 1024) maxPhotos = 4;
                              return {
                                maxPhotos,
                                minPhotos: 1,
                                singleRowMaxHeight: 350,
                              };
                            }}
                            onClick={({ index }) => {
                              handleOpenFullImage(subcategory.photos, index);
                            }}
                            render={{
                              button: (props, context) => {
                                const { index } = context;
                                const currentPhoto = subcategory.photos[index];
                                const altText = currentPhoto.alt_text || "";
                                const ariaLabel = altText
                                  ? `Photo ${index + 1} of ${subcategory.photos.length} in ${subcategory.name}. ${altText}`
                                  : `Photo ${index + 1} of ${subcategory.photos.length} in ${subcategory.name}`;

                                return (
                                  <button
                                    {...props}
                                    aria-label={ariaLabel}
                                    className={`${props.className} rounded-lg relative overflow-hidden group`}
                                    style={{
                                      ...props.style,
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenFullImage(
                                          subcategory.photos,
                                          index,
                                        );
                                      }
                                    }}
                                  >
                                    {props.children}
                                    <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded-lg" />
                                  </button>
                                );
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Flag Photo Modal */}
      <FlagVenuePhoto
        photoReviewId={selectedImage?.review_photos_id}
        venueName={venueDetails?.name}
        isOpen={isFlagVenuePhotoOpen}
        onClose={() => handleFlagVenuePhotoClose()}
        venueDetails={{
          data: {
            id: venueDetails?.id,
            brandId: venueDetails?.brandId,
            brandName: venueDetails?.brandName,
            address: venueDetails?.address,
            name: venueDetails?.name,
            types: venueDetails?.types,
            business_photos: venueDetails?.businessPhotos,
          },
        }}
      />

      {/* Full Image Viewer */}
      {fullImageView && currentSubcategoryPhotos.length > 0 && (
        <FullImageViewer
          images={currentSubcategoryPhotos.map((photo) => ({
            src: bucketUrlPrefix + photo.path,
            alt: photo.alt_text || "",
            tag_name: photo.tag_name,
            category_name: photo.category_name,
            created_at: photo.created_at,
            photo_type: photo.photo_type,
            review_photos_id: photo.review_photos_id,
            width: photo.width || 800,
            height: photo.height || 600,
            sort_order: photo.sort_order,
            qtags: photo.qtags || [],
            tags: photo.tags || [
              {
                tagName: photo.tag_name || "Uncategorized",
                categoryDisplaySectionId: "",
              },
            ],
            displaySections: photo.displaySections,
            venueId: venueDetails?.id,
            venueName: venueDetails?.name,
            venueAddress: venueDetails?.address,
            venueBrandId: venueDetails?.brandId,
            venueBrandName: venueDetails?.brandName,
            venueTypes: venueDetails?.types,
            user_roles: photo.user_roles,
          }))}
          currentIndex={currentImageIndex}
          onClose={() => setFullImageView(false)}
          onFlag={handleFlagPhoto}
          showFlag={true}
          showRightTab={true}
        />
      )}
    </div>
  );
}

export default PhotosDetailView;
